import { Trans, useI18n } from '@i18n';
import { Button } from '@mui/material';
import { PropsWithChildren } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import { IMAGES } from '@/assets';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { faro } from '@/faro';

type Props = {
  pageName: string;
};

/**
 * A generic boundary for a particular page.
 * The user can still use other parts of the application.
 */
function PageErrorBoundary({ children, pageName }: PropsWithChildren<Props>) {
  const { t } = useI18n();
  const navigate = useNavigate();

  return (
    <ErrorBoundary
      onError={(error, info) => {
        faro?.api?.pushError(error);
        console.error({ error, info });
      }}
      key={pageName}
      fallbackRender={({ resetErrorBoundary }) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '2rem',
            // respecting the padding of the main component
            height: 'calc(100vh - 84px)',
          }}
        >
          <img src={IMAGES.ErrorFallbackPage} alt="A file icon with a smiley looking in a unsatisfied way" />
          <PTypography variant={'h3'}>{t('General_AnErrorHasOccurred')}</PTypography>
          <PTypography variant={'body1'} align="center" sx={{ maxWidth: 600 }}>
            <Trans i18nKey="PageErrorBoundary_Text" values={{ pageName: pageName }} components={{ bold: <strong /> }} />
          </PTypography>

          <Button onClick={resetErrorBoundary}>{t('General_TryAgain')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              navigate(-1);
              resetErrorBoundary();
            }}
          >
            {t('General_ReturnToPreviousPage')}
          </Button>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

export default PageErrorBoundary;
